import { useContext, useState } from 'react'
import { useParams } from 'react-router-dom'
import { Building2, ChevronRight } from 'lucide-react'
import { AuthContext } from '@retink/web-auth'
import { api, switchTenant, WORKSPACE_URL } from '../../lib/api'
import { useResource } from '@retink/web-ui'
import { useMutation } from '@retink/web-ui'
import { statusTone } from '@retink/web-ui'
import { drillRow } from '@retink/web-ui'
import { Button } from '@retink/web-ui'
import { Badge } from '@retink/web-ui'
import { Input, Field } from '@retink/web-ui'
import { Card, CardHeader, Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@retink/web-ui'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@retink/web-ui'
import { PageHeader } from '@retink/web-ui'
import { EmptyState, TableEmpty } from '@retink/web-ui'
import { LoadingState, ErrorState } from '@retink/web-ui'
import { FormError } from '@retink/web-ui'
import { toast } from '@retink/web-ui'
import { IdentifierPreview, dnsLabelOk } from '../../components/IdentifierPreview'

interface Tenant {
  id: string; name: string; slug: string; status: string; region: string
  plan_name: string | null; member_count: number
}

// Customer-facing workspaces. `tenant` remains internal API/database vocabulary.
export function OrgTenants() {
  const { user } = useContext(AuthContext)
  const { orgId } = useParams<{ orgId: string }>()
  const tenantsUrl = `/api/orgs/${orgId}/tenants`
  const { data: tenants = [], loading, error, reload } = useResource(() => api.get<Tenant[]>(tenantsUrl), [orgId])
  const [showCreate, setShowCreate] = useState(false)
  const [form, setForm] = useState({ name: '', region: 'northeurope' })

  // No slug field — the server derives the tenant slug from its name.
  const createTenant = useMutation(() => {
    const tenant = { name: form.name, region: form.region }
    return api.post(`/api/orgs/${orgId}/tenants`, tenant)
  }, { fallbackError: 'Failed' })
  async function create(e: React.FormEvent) {
    e.preventDefault()
    if (await createTenant.mutate() === undefined) return
    setShowCreate(false)
    setForm({ name: '', region: 'northeurope' })
    toast.success('Workspace created')
    reload()
  }

  return (
    <div className="p-8">
      <PageHeader
        title="Workspaces"
        description="Operating areas owned by this organization. Microsoft sign-in is inherited automatically."
        action={<Button onClick={() => { createTenant.reset(); setShowCreate(true) }}>+ New workspace</Button>}
      />

      <Card className="overflow-hidden">
        <CardHeader>Workspaces</CardHeader>
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              {['Workspace', 'People', 'Plan', 'Status', ''].map((h, i) => (
                <TableHead key={h || i}>{h}</TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && <TableEmpty colSpan={5}><LoadingState /></TableEmpty>}
            {!loading && error && <TableEmpty colSpan={5}><ErrorState message={error} onRetry={reload} /></TableEmpty>}
            {!loading && !error && tenants.length === 0 && (
              <TableEmpty colSpan={5}>
                <EmptyState
                  icon={Building2}
                  title="No workspaces yet"
                  description="Create the first workspace. It inherits the organization's active Microsoft sign-in."
                  action={<Button onClick={() => { createTenant.reset(); setShowCreate(true) }}>+ New workspace</Button>}
                />
              </TableEmpty>
            )}
            {tenants.map((t) => (
              <TableRow key={t.id} {...drillRow(() => switchTenant(t.id, `${WORKSPACE_URL}/dashboard`))}>
                <TableCell className="font-medium">
                  <span className="text-foreground transition-colors group-hover:text-primary">{t.name}</span>
                  <span className="ml-1 text-xs text-muted-foreground font-mono">{t.slug}</span>
                </TableCell>
                <TableCell className="text-muted-foreground">{t.member_count}</TableCell>
                <TableCell className="text-muted-foreground">{t.plan_name ?? '—'}</TableCell>
                <TableCell><Badge tone={statusTone(t.status)}>{t.status}</Badge></TableCell>
                <TableCell className="text-right text-muted-foreground">
                  <ChevronRight className="inline size-4 opacity-0 transition-opacity group-hover:opacity-60" aria-hidden />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>New workspace</DialogTitle>
            <DialogDescription>Identity inherits from the organization. Internal plan controls do not block creation.</DialogDescription>
          </DialogHeader>
          <form onSubmit={create} className="space-y-4">
            <FormError>{createTenant.error}</FormError>
            <Field label="Name">
              <Input value={form.name} required autoFocus
                onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </Field>
            <IdentifierPreview name={form.name} className="-mt-2" />
            <Field label="Region">
              <Input value={form.region}
                onChange={(e) => setForm({ ...form, region: e.target.value })} />
            </Field>
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="ghost" onClick={() => setShowCreate(false)}>Cancel</Button>
              <Button type="submit" disabled={createTenant.saving || !dnsLabelOk(form.name)}>{createTenant.saving ? 'Creating…' : 'Create workspace'}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  )
}
